/* eslint react-hooks/immutability: ["warn"] */
/* eslint react-hooks/set-state-in-effect: ["warn"] */
import { useEffect, useState, useCallback, useMemo } from "react";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useTranslation } from "@coworker/locales";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { Media } from "../types/media";
import { MediaSettingsForm } from "../Organisms/MediaSettingsForm";
import { FullWidthButton } from "../../SkapaButton";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../hooks/queryKeys";
import { useMediasByStoreId } from "../hooks/useMediasByStoreId";
import { useParams } from "react-router";
import { useMediaById } from "../hooks/useMediaById";
import { getRequiredFields, getMediaTypeText } from "../tools";
import {
  checkEmptyMediaName,
  checkMediaName,
} from "../Organisms/Settings/SettingsForm.helper";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { useImagesByMediaId } from "../hooks/useImagesByMediaId";
import { useUpdateMedia } from "../hooks/useUpdateMedia";
import { MessageModal } from "../Organisms/Modals/MessageModal";
import { OkCancelModal } from "../Organisms/Modals/OkCancelModal";
import { MediaTypes } from "../constants";
import { CenterContainerWithGap } from "../styles/styles";
import { ChangeMediaTypeConfirmationDialog } from "../Organisms/Modals/ChangeMediaTypeConfirmationModal";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import {
  useImageUploader,
  useWaitForImageUpload,
} from "@coworker/apprestructured/src/uploader/useImageUploader";
import { useWriteSignedUrls } from "../hooks/useWriteSignedUrls";
import { imageToExistingFile } from "../Helpers/imageHelpers";
import { useSyncMediaImages } from "../hooks/useSyncMediaImages";
import SomethingWentWrongPrompt from "@coworker/apprestructured/src/task-management/components/SomethingWentWrongPrompt/SomethingWentWrongPrompt";

export function UpdateMediaView() {
  const { t } = useTranslation();

  const { mediaId } = useParams();
  const navigate = useFixaNavigate();

  const { data: mediaResponse } = useMediaById(mediaId);
  const updateMediaMutation = useUpdateMedia(mediaId || "");
  const [showModalMessage, setShowModalMessage] = useState("");
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [showChangeMediaTypeModal, setShowChangeMediaTypeModal] =
    useState(false);
  const [currentMedia, setCurrentMedia] = useState({} as Media);
  const [currentCheckedMediaType, setCurrentCheckedMediaType] =
    useState<MediaTypes>(MediaTypes.NONE);
  const [originalMediaType, setOriginalMediaType] = useState(MediaTypes.NONE);

  const { data: mediasResponse } = useMediasByStoreId(currentMedia.storeId);
  const queryClient = useQueryClient();
  const { data: images } = useImagesByMediaId(currentMedia.id);
  const { showToast } = useToastNotification();
  const [storedImages, setStoredImages] = useState<NewOrExistingFile[]>([]);
  const [currentImages, setCurrentImages] = useState<NewOrExistingFile[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const useWriteSignedUrlsFunc = useMemo(() => useWriteSignedUrls, []);
  const imageSize = useMemo(() => ({ maxWidthOrHeight: 1024 }), []);

  const imageUploader = useImageUploader(
    currentImages,
    imageSize,
    useWriteSignedUrlsFunc
  );

  const { upload: uploadImages } = useWaitForImageUpload(() => imageUploader);
  const { createUploads: imagesCreateUploads, error: uploadImagesError } =
    imageUploader;

  const { syncMediaImages, error: syncMediaImagesError } = useSyncMediaImages({
    mediaId: currentMedia.id,
    storedImages,
    currentImages,
    imagesCreateUploads,
  });

  useEffect(() => {
    if (images && storedImages.length === 0) {
      const convertedImages = images.map(imageToExistingFile);
      setStoredImages(convertedImages);
      setCurrentImages(convertedImages.map((img) => ({ ...img })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const linkBarItems = [
    {
      label: t("mediaMaintenanceString"),
      to: "/mediamaintenance",
    },
  ];
  if (mediaResponse) {
    if (mediaResponse.parent) {
      linkBarItems.push({
        label: mediaResponse.parent.name,
        to: `/mediamaintenance/tabs/${mediaResponse.parent.id}`,
      });
    }
    linkBarItems.push({
      label: mediaResponse?.media.name ?? t("mediaNameString"),
      to: `/mediamaintenance/tabs/${mediaId}`,
    });
  }

  const linkbar = (
    <Linkbar currentLabel={t("updateMediaString")} items={linkBarItems} />
  );

  const [mediaInitialized, setMediaInitialized] = useState(false);

  useEffect(() => {
    if (mediaResponse && !mediaInitialized) {
      setCurrentMedia(mediaResponse.media);
      setCurrentCheckedMediaType(mediaResponse.media.mediaType as MediaTypes);
      setOriginalMediaType(mediaResponse.media.mediaType as MediaTypes);
      setMediaInitialized(true);
    }
  }, [mediaResponse, mediaInitialized]);

  const handleImagesChange = (newImages: NewOrExistingFile[]) => {
    setCurrentImages(newImages);
  };

  function validateMediaFields(media: Media): boolean {
    const requiredKeys = getRequiredFields(media);
    return requiredKeys.every((key) => {
      const value = media[key as keyof Media];
      return value;
    });
  }

  const [showErrorMessage, setshowErrorMessage] = useState(true);

  const ValidateMedia = useCallback(() => {
    const isDisabled =
      checkEmptyMediaName(currentMedia) ||
      checkMediaName(currentMedia, mediasResponse?.medias || []) ||
      !validateMediaFields(currentMedia);

    setshowErrorMessage(isDisabled);
  }, [currentMedia, mediasResponse]);

  useEffect(() => {
    ValidateMedia();
  }, [ValidateMedia]);

  const submitDisabled = isSaving || showErrorMessage;

  const onSubmit = async () => {
    if (showErrorMessage) return;
    setIsSaving(true);
    try {
      await uploadImages();
      await syncMediaImages();
      await updateMediaMutation.mutateAsync(currentMedia);
      showToast(
        t("nameWasUpdatedString", {
          name: getMediaTypeText(currentMedia.mediaType as MediaTypes),
        })
      );
      navigate(`/mediamaintenance/tabs/${mediaId}`);
    } catch (error) {
      console.log(error);
      setShowModalMessage(t("couldNotUpdateRoomTryAgainString"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentMedia) {
    return <></>;
  }
  const showSomethingWentWrongPrompt: boolean =
    !!uploadImagesError || !!syncMediaImagesError;

  const handleRefreshClick = () => {
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  return (
    <FullScreenPopup
      noPadding
      linkbar={linkbar}
      fullHeightContent={true}
      fieldsAreEmpty={showErrorMessage}
      actionBarContent={
        <CenterContainerWithGap $centerText={true}>
          <FullWidthButton
            type="secondary"
            text={t("cancelString")}
            onClick={() => {
              if (!showErrorMessage) {
                setShowConfirmCloseModal(true);
              } else {
                navigate(`/mediamaintenance/tabs/${mediaId}`);
              }
            }}
            loading={showConfirmCloseModal || isSaving}
          />
          <FullWidthButton
            type="primary"
            text={t("saveString")}
            onClick={() => {
              if (
                originalMediaType !== currentMedia.mediaType &&
                !showErrorMessage
              ) {
                setShowChangeMediaTypeModal(true);
              } else {
                onSubmit();
              }
            }}
            loading={showChangeMediaTypeModal || isSaving}
            disabled={submitDisabled}
          />
        </CenterContainerWithGap>
      }
    >
      <MediaSettingsForm
        currentMedia={currentMedia}
        medias={mediasResponse?.medias || []}
        setCurrentMedia={(newMedia) => {
          setCurrentMedia(newMedia);
        }}
        currentCheckedMediaType={currentCheckedMediaType}
        setCurrentCheckedMediaType={setCurrentCheckedMediaType}
        images={currentImages}
        onImageUpdate={() => {}}
        isUpdate={true}
        onImagesChange={handleImagesChange}
      />
      {!!showModalMessage && (
        <MessageModal
          message={showModalMessage}
          closeModal={() => {
            setShowModalMessage("");
          }}
        />
      )}
      {showConfirmCloseModal && (
        <OkCancelModal
          onOk={() => {
            setShowConfirmCloseModal(false);
            navigate(`/mediamaintenance/tabs/${mediaId}`);
            setCurrentMedia(mediaResponse?.media ?? ({} as Media));
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.ImagesByMedia, mediaId],
            });
          }}
          onCancel={() => {
            setShowConfirmCloseModal(false);
          }}
          title={t("confirmCloseString")}
          text={t("allChangesLostCloseString")}
        />
      )}
      {showChangeMediaTypeModal && (
        <ChangeMediaTypeConfirmationDialog
          onConfirm={async () => {
            await onSubmit();
            setShowChangeMediaTypeModal(false);
          }}
          onCancel={() => {
            setShowChangeMediaTypeModal(false);
          }}
          media={currentMedia}
          originalMediaType={originalMediaType}
        />
      )}
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
    </FullScreenPopup>
  );
}
